import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { FontStyle, Text } from '~/components/core'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import {
  usePartnerDesktopAppDownloadUrl,
  usePartnerLogo,
} from '~/hooks/useCurrentPartner'
import {
  ContentWrapper,
  LoginButtonWrapper,
  LogoImage,
} from '~/pages/Login/Login.styles'
import { LoginBackground } from '~/pages/Login/LoginBackground'
import { Size } from '~/types/ThemeTypes'
import { openDesktopApp, resolveEmailDeepLink } from '~/utils/desktopDeepLink'
import { openInNewTab } from '~/utils/functions'

import { CopyStack, FallbackStack, wrappingCopyStyle } from './OpenApp.styles'

/**
 * Public, unauthenticated interstitial that email CTAs link to (webmail strips
 * raw `mango://` hrefs). `?to=<key>` names the destination; on mount the page
 * resolves it to a deep link (EMAIL_DEEP_LINK_TARGETS) and hands it to the OS.
 * Unknown/missing `to` falls back to the default, so legacy `/desktop-update`
 * links keep working.
 *
 * We don't show the emailed version/channel: the deep link only tells the app to
 * check its own channel, so printed values could contradict what it does. And we
 * don't detect whether the app opened — browsers give no reliable signal, so the
 * download link is always shown rather than guessed at.
 */
export const OpenAppPage = () => {
  const { t } = useTranslation()
  const partnerLogo = usePartnerLogo()
  const downloadUrl = usePartnerDesktopAppDownloadUrl()
  const [searchParams] = useSearchParams()

  const deepLink = useMemo(
    () => resolveEmailDeepLink(searchParams.get('to')),
    [searchParams],
  )

  const launchApp = useCallback(() => {
    openDesktopApp(deepLink)
  }, [deepLink])

  useEffect(() => {
    launchApp()
  }, [launchApp])

  return (
    <LoginBackground>
      <ContentWrapper>
        <LogoImage alt="logo" src={partnerLogo} />
        <CopyStack>
          <Text
            fontStyle={FontStyle.Medium}
            align="center"
            text={t('openApp.title', 'Opening your desktop app…')}
          />
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            style={wrappingCopyStyle}
            text={t(
              'openApp.subtitle',
              "We're launching the app now. If it doesn't open automatically, use the button below.",
            )}
          />
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            text={t(
              'openApp.closeHint',
              'You can close this tab once the app opens.',
            )}
          />
        </CopyStack>
        <LoginButtonWrapper>
          <MangoButton onClick={launchApp} size={Size.md}>
            {t('openApp.openButton', 'Open desktop app')}
          </MangoButton>
        </LoginButtonWrapper>
        <FallbackStack>
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            color="var(--chakra-colors-fg-muted)"
            text={t(
              'openApp.fallbackPrompt',
              "Don't have the desktop app installed?",
            )}
          />
          <MangoButton
            variant={MangoButtonVariant.Brand_Text}
            size={Size.sm}
            onClick={() => openInNewTab(downloadUrl)}
          >
            {t('openApp.downloadButton', 'Download the desktop app')}
          </MangoButton>
        </FallbackStack>
      </ContentWrapper>
    </LoginBackground>
  )
}
